import React, { useState, useEffect, useRef } from 'react'
import './UnitDetailPage.css'
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import httpClient from '../components/helpers/httpClient'
import { toast } from "react-toastify"
import MenuBar from "../components/MenuBar"
import Header from "../components/Header"
import LoadingSpinner from '../components/placeholders/LoadingSpinner'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import GeneralSection from '../components/popup-window-unit-detail/components/GeneralSection'
import PropertySection from '../components/popup-window-unit-detail/components/PropertySection'
import RentalSection from '../components/popup-window-unit-detail/components/RentalSection'
import PurchaseSection from '../components/popup-window-unit-detail/components/purchase-section/PurchaseSection'
import PriceSection from '../components/popup-window-unit-detail/components/PriceSection'
import DocumentsSection from '../components/DocumentsSection'
import Button from '../components/UI/Button'
import UnitTransactionsSection from '../components/popup-window-unit-detail/components/UnitTransactionsSection'

function UnitDetailPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { projectID, buildingID, unitID } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const isNewUnit = unitID === 'new'
  const actionType = isNewUnit ? 'add' : 'edit'
  const availableMenuItems = isNewUnit
    ? ['general']
    : ['general', 'price', 'property', 'transactions', 'rental', 'purchase', 'documents']

  const getMenuItemFromSearch = (search) => {
    const section = new URLSearchParams(search).get('section')
    return availableMenuItems.includes(section) ? section : 'general'
  }

  const initialUnitState = {
    unit_name: '',
    address: '',
    building_id: buildingID || '',
    status: '',
    type: '',
    habitable_area_m2: '',
    terrace_area_m2: '',
    garden_area_m2: '',
    floor_level: ''
  }

  const [unit, setUnit] = useState(initialUnitState)
  const [project, setProject] = useState({})
  const [building, setBuilding] = useState(null)
  const [contactsList, setContactsList] = useState([{ "id": null, "first_name": null, "last_name": null }])
  const [menuItem, setMenuItem] = useState(() => getMenuItemFromSearch(location.search))
  const [loading, setLoading] = useState(true)
  const [creatingNote, setCreatingNote] = useState(false)
  const [creatingProperty, setCreatingProperty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const purchaseSectionRef = useRef(null)
  const generalSectionRef = useRef()
  const priceSectionRef = useRef(null)
  const propertySectionRef = useRef(null)

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([
        fetchProject(),
        fetchFullContacts(),
        !isNewUnit && fetchUnit()
      ])
      setLoading(false)
    }
    loadData()
  }, [projectID, buildingID, unitID])

  useEffect(() => {
    const nextMenuItem = getMenuItemFromSearch(location.search)
    setMenuItem(nextMenuItem)

    const currentSection = new URLSearchParams(location.search).get('section')
    if (currentSection === nextMenuItem) {
      return
    }

    const nextParams = new URLSearchParams(location.search)
    nextParams.set('section', nextMenuItem)
    setSearchParams(nextParams, { replace: true })
  }, [location.search, isNewUnit, setSearchParams])

  const fetchProject = async () => {
    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/projects/get/${projectID}`)
      const data = response.data
      setProject(data)
      
      // Find the building
      if (data.buildings && buildingID) {
        const foundBuilding = data.buildings.find(b => b.id == buildingID)
        setBuilding(foundBuilding)
      }
    } catch (error) {
      console.error('Error fetching project:', error)
      toast.error('Failed to load project data')
    }
  }

  const fetchUnit = async () => {
    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/units/get/${unitID}`)
      setUnit({ ...initialUnitState, ...response.data, building_id: buildingID })
    } catch (error) {
      console.error('Error fetching unit:', error)
      toast.error('Failed to load unit data')
    }
  }

  const fetchFullContacts = async () => {
    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + "/api/contacts/get")
      setContactsList(response.data)
    } catch (error) {
      console.error('Error fetching contacts:', error)
    }
  }

  const handleGoBack = () => {
    // Match other detail pages: go back in history when possible.
    // Fallback to the project page if user landed here directly (no meaningful back stack).
    const historyIdx = window.history?.state?.idx
    if (typeof historyIdx === 'number' && historyIdx > 0) {
      navigate(-1)
      return
    }
    navigate(`/projects/${projectID}`)
  }

  const handleSave = async () => {
    if (isSaving) return

    setIsSaving(true)

    try {
      if (creatingNote) {
        await generalSectionRef.current.handleCreateNote()
        setIsSaving(false)
        return
      }

      if (creatingProperty) {
        await propertySectionRef.current.handleCreateProperty()
        setIsSaving(false)
        return
      }

      if (!unit.unit_name) {
        toast.error("Name is obligatory")
        setIsSaving(false)
        return
      }

      const saveUnit = httpClient.post(
        import.meta.env.VITE_API_URL + `/api/units/create?itemID=${isNewUnit ? null : unitID}&actionType=${actionType}`,
        unit
      )

      let savePurchaseOrders = Promise.resolve()
      if (purchaseSectionRef.current) {
        savePurchaseOrders = purchaseSectionRef.current.handleSavePurchaseOrderLines?.() || Promise.resolve()
      }

      let savePriceDetails = Promise.resolve()
      if (priceSectionRef.current) {
        savePriceDetails = priceSectionRef.current.handleSavePriceDetails()
      }

      const [unitResponse] = await toast.promise(
        Promise.all([saveUnit, savePurchaseOrders, savePriceDetails]),
        {
          success: `Unit has been ${isNewUnit ? "created" : "updated"}!`,
          error: { render({ data }) { return data?.message || 'Something went wrong' } },
          pending: `${isNewUnit ? "Creating" : "Updating"} unit...`
        }
      )

      // If creating new unit, navigate to the edit page
      if (isNewUnit && unitResponse?.data?.id) {
        const nextSearch = new URLSearchParams(location.search)
        nextSearch.set('section', menuItem)
        const search = nextSearch.toString()

        navigate(
          `/projects/${projectID}/buildings/${buildingID}/units/${unitResponse.data.id}${search ? `?${search}` : ''}`,
          { replace: true }
        )
      }
    } catch (error) {
      console.error('Error saving unit:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSetMenuItem = (item) => {
    const nextMenuItem = availableMenuItems.includes(item) ? item : 'general'
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('section', nextMenuItem)

    setMenuItem(nextMenuItem)
    setSearchParams(nextParams)
    setCreatingProperty(false)
    setCreatingNote(false)
  }

  const getSaveButtonText = () => {
    if (isSaving) return "SAVING..."
    if (creatingNote) return "CREATE NOTE"
    if (creatingProperty) return "CREATE KI"
    return "SAVE"
  }

  const getPageTitle = () => {
    const projectName = project.project_commercial_name || project.project_placeholder_name || ''
    const buildingName = building?.building_name || ''
    const unitName = unit?.unit_name || (isNewUnit ? 'New Unit' : '')
    
    return [projectName, buildingName, unitName].filter(Boolean).join(' | ')
  }

  if (loading) {
    return (
      <div className='template'>
        <Header>
          <div className='header__left'>
            <div className="back_button" onClick={handleGoBack}>
              <KeyboardBackspaceIcon /> Go Back
            </div>
          </div>
        </Header>
        <MenuBar />
        <div className="template__body" id='body-unit-detail'>
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  return (
    <div className='template'>
      <Header>
        <div className='header__left'>
          <div className="back_button" onClick={handleGoBack}>
            <KeyboardBackspaceIcon /> Go Back
          </div>
          <div className="header__page-title">{getPageTitle()}</div>
        </div>
        <div className='header__options--right'>
          <Button
            variant='primary'
            size='sm'
            onClick={handleSave}
            disabled={isSaving}
            style={{ opacity: isSaving ? 0.6 : 1 }}
          >
            {getSaveButtonText()}
          </Button>
        </div>
      </Header>

      <MenuBar />

      <div className="template__body" id='body-unit-detail'>
        <div className='menu__titles'>
          <h3
            className={`${menuItem === "general" ? "selected" : ""}`}
            onClick={() => handleSetMenuItem("general")}
          >
            general
          </h3>
          {!isNewUnit && (
            <>
              <h3
                className={`${menuItem === "price" ? "selected" : ""}`}
                onClick={() => handleSetMenuItem("price")}
              >
                price details
              </h3>
              <h3
                className={`${menuItem === "property" ? "selected" : ""}`}
                onClick={() => handleSetMenuItem("property")}
              >
                property details
              </h3>
              <h3
                className={`${menuItem === "transactions" ? "selected" : ""}`}
                onClick={() => handleSetMenuItem("transactions")}
              >
                transactions
              </h3>
              <h3
                className={`${menuItem === "rental" ? "selected" : ""}`}
                onClick={() => handleSetMenuItem("rental")}
              >
                rental details
              </h3>
              <h3
                className={`${menuItem === "purchase" ? "selected" : ""}`}
                onClick={() => handleSetMenuItem("purchase")}
              >
                purchase orders
              </h3>
              <h3
                className={`${menuItem === "documents" ? "selected" : ""}`}
                onClick={() => handleSetMenuItem("documents")}
              >
                documents
              </h3>
            </>
          )}
        </div>

        <div className={`body__section ${menuItem}`}>
          {menuItem === "general" && (
            <GeneralSection
              ref={generalSectionRef}
              unit={unit}
              setUnit={setUnit}
              actionType={actionType}
              contactsList={contactsList}
              loading={{ general: false, properties: false }}
              creatingNote={creatingNote}
              setCreatingNote={setCreatingNote}
            />
          )}

          {menuItem === "property" && (
            <PropertySection
              ref={propertySectionRef}
              unit={unit}
              loading={{ general: false, properties: false }}
              creatingProperty={creatingProperty}
              setCreatingProperty={setCreatingProperty}
            />
          )}

          {menuItem === "rental" && (
            <RentalSection unit={unit} />
          )}

          {menuItem === "transactions" && (
            <UnitTransactionsSection unit={unit} />
          )}

          {menuItem === "price" && (
            <PriceSection
              ref={priceSectionRef}
              unit={unit}
              loading={{ general: false, properties: false }}
            />
          )}

          {menuItem === "purchase" && (
            <PurchaseSection
              ref={purchaseSectionRef}
              unit={unit}
            />
          )}

          {menuItem === "documents" && (
            <DocumentsSection scope="unit" entityId={unitID} editable={true} showRentalContractSelect={true} />
          )}
        </div>
      </div>
    </div>
  )
}

export default UnitDetailPage
